import React, { useState } from 'react';
import { createContext } from 'react';
import i18n from '~shared/utils/i18n';
import isSameDay from 'date-fns/is_same_day';
import compareAsc from 'date-fns/compare_asc';
import differenceInMinutes from 'date-fns/difference_in_minutes';
import isValid from 'date-fns/is_valid';
import { get } from '~shared/utils/http';
import { seoUrlEvent } from '~spa/Utils/SeoUrls';
import Time from '~shared/ui/Time/Time';
import { getLocalizedDateFormat } from '~shared/utils/date-time';
import { getIsBogRestricted } from '~shared/utils/user';
import { buildSpectateUrl } from '~spa/Utils/Spectate';
import { createMenuTabReact } from '~shared/components/menu-tab/createMenuTab';
import {
    DATE_TIME_FORMAT_DEFAULT,
    DATE_TIME_FORMAT_DD_MMM,
    DATE_TIME_FORMAT_DDD,
} from '~shared/utils/constants';
import {
    SAME,
    GREATER_THAN,
    LESS_THAN,
    SCHEDULE_URL,
    BOG_CATEGORIES,
} from './constants';

export const ScheduleWidgetContext = createContext(null);

export const getUTCDateTime = (date) => {
    if (!(date instanceof Date) || isNaN(date)) {
        return null;
    }

    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
    );
};

export const getContent = async (slug, activeTab = 'today') => {
    const tab = `tab=${activeTab}`;

    const response = await get(
        buildSpectateUrl(`${SCHEDULE_URL}${slug}?${tab}`)
    );

    return response.json();
};

export const scrollToEvent = (eventNode, container) => {
    container.scrollBy({
        left: eventNode.parentNode.offsetLeft,
        behavior: 'smooth',
    });
};

export const getEventUrl = (sport, event) => {
    if (!event) { return '/'; }
    const {
        category_slug_i18n: category,
        tournament_slug_i18n: tournament,
        event_slug_i18n: slug,
        is_antepost: isAntepost,
        id,
    } = event;

    return seoUrlEvent(
        `${sport}/${category}/${isAntepost ? i18n.t('url.ante-post') : tournament}/${slug}`,
        id,
    );
};

export const useTrigger = () => {
    const [state, setState] = useState(false);

    const trigger = () => {
        setState(true);
    };

    const done = () => {
        setState(false);
    };

    return { state, trigger, done };
};

const hasUpcomingEventsCompare = (a, b) => {
    const { has_upcoming_events: first } = a;
    const { has_upcoming_events: second } = b;

    if (first === second) {
        return SAME;
    }
    if (second === true) {
        return GREATER_THAN;
    }

    return LESS_THAN;
};

export const sortByUpcomingEvent = (list) => [...list].sort(hasUpcomingEventsCompare);

const eventDateCompare = (eventsDetails) => (event1, event2) => {
    const { scheduled_start: event1Date } = eventsDetails[event1];
    const { scheduled_start: event2Date } = eventsDetails[event2];

    // ISO-8601 dates can be sorted lexicographically.
    if (event1Date < event2Date) {
        return LESS_THAN;
    }

    return GREATER_THAN;
};

export const getCategoryEvents = (tournaments, eventsDetails) => {
    return tournaments.reduce((tournamentEvents, tournament) => [...tournamentEvents, ...tournament.events], [])
        .sort(eventDateCompare(eventsDetails));
};

export const getTournamentsEvents = (tournaments) => {
    return tournaments.reduce((acc, tournament) => {
        const { events } = tournament;

        return [...acc, ...( events || []) ];
    }, []);
};

// eslint-disable-next-line react/display-name
export const getTimeToRacingMessage = (t) => (events, eventsDetails, isFuture, showDate = true) => {
    const now = new Date(Date.now());
    const hour = 60;
    const imminent = 0;
    const imminentEvents = events.filter(eventId => {
        const { scheduled_start: scheduledStartUTC } = eventsDetails[eventId];
        const scheduledStart = new Date(scheduledStartUTC);
        const diff = differenceInMinutes(scheduledStart, now);

        return diff <= imminent;
    });

    const first = 0;
    const firstEvent = events[first];
    const upcomingEvents = events.filter(eventId => parseBoolean(eventsDetails[eventId].active));
    const upcomingNotImminentEvent = upcomingEvents.filter(eventId => !imminentEvents.includes(eventId))[first];

    if (!upcomingNotImminentEvent) { // If there are only imminent events
        if (upcomingEvents.length) {
            if (firstEvent === upcomingEvents[first]) {
                return t('racing.race_soon');
            }

            return t('racing.next_race_soon');

        }
        const doneEvents = events.filter(id => parseBoolean(eventsDetails[id].hasResults));
        if (doneEvents.length) {
            return t('racing.over_results');
        }

        return t('racing.over');

    } // if there are upcoming events that are not imminent
    const { scheduled_start: scheduledStartUTC } = eventsDetails[upcomingNotImminentEvent];
    const scheduledStart = new Date(scheduledStartUTC);

    if (isSameDay(scheduledStart, now)) {
        let messageKey;
        let timeRemainingMsg;
        const timeRemaining = differenceInMinutes(scheduledStart, now);
        if (firstEvent === upcomingNotImminentEvent) {
            messageKey = 'racing.racing_in';
        } else {
            messageKey = 'racing.next_race_in';
        }
        if (timeRemaining >= hour) { //at least one hour
            timeRemainingMsg = t('racing.hr', { n: Math.round(timeRemaining / hour) });

            return t(messageKey, { interval: timeRemainingMsg });
        }
        if (timeRemaining >= imminent) {
            timeRemainingMsg = t('racing.min', { n: timeRemaining });

            return t(messageKey, { interval: timeRemainingMsg });
        }

    } else if (showDate){
        const format = !isFuture ? DATE_TIME_FORMAT_DEFAULT : DATE_TIME_FORMAT_DD_MMM;
        const localizedFormat = getLocalizedDateFormat(format);

        return <Time dateTime={scheduledStartUTC} format={localizedFormat} />;
    }

    return null;
};

export const parseBoolean = (bool) => {
    const one = 1;

    return bool === true || parseInt(bool, 10) === one;
};

export const getBogInfo = (categorySlug) => {
    const isBogRestricted = getIsBogRestricted();
    const isBogCategory = BOG_CATEGORIES.includes(categorySlug);
    const isBogAllowed = isBogCategory && !isBogRestricted;

    return [isBogCategory, isBogAllowed];
};

const parseContent = (t, label) => {
    if (isValid(new Date(label))) {
        return (
            <>
                <Time dateTime={`${label}+00:00`} format={getLocalizedDateFormat(DATE_TIME_FORMAT_DDD)} />
                {' '}
                <Time dateTime={`${label}+00:00`} format={getLocalizedDateFormat(DATE_TIME_FORMAT_DD_MMM)} />
            </>
        );
    }
    if(label === 'tomorrow'){
        return t(`racing.${label}`);
    }

    return t(`racing.${label}`);
};


export const getTabContent = (t, label) => {
    const content = parseContent(t, label);
    const { el: tab, ...menu } = createMenuTabReact(content);

    return {tab, ...menu};
};


export const sortDates = (dates) => {
    return dates.sort((dateStringA, dateStringB) => {
        const dateA = new Date(dateStringA);
        const dateB = new Date(dateStringB);

        return compareAsc(dateA, dateB);
    });
};
